import React, { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import CTAButton from "@/components/ui/CTAButton";
import { useCookieConsent } from "@/context/CookieConsentContext";
import { cn } from "@/lib/utils";

const SELECTOR_FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const CATEGORIES = [
  {
    key: "necessary",
    title: "Necesarias",
    locked: true,
    description:
      "Imprescindibles para que la web funcione: sesión, seguridad y recordar tu elección de cookies.",
  },
  {
    key: "analytics",
    title: "Analíticas",
    description:
      "Nos ayudan a saber qué páginas se visitan más y a mejorar los contenidos (p. ej. Google Analytics).",
  },
  {
    key: "personalization",
    title: "Personalización",
    description: "Recuerdan preferencias como el idioma o la configuración de visualización.",
  },
  {
    key: "marketing",
    title: "Publicitarias y de terceros",
    description:
      "Usadas por redes sociales y plataformas externas para mostrar contenido relevante.",
  },
];

/**
 * Aviso de cookies (RGPD / LSSI-CE), en dos capas:
 *
 *  1. Aviso simple, con las tres opciones a la vista y con el mismo peso
 *     visual: aceptar todas, rechazar las no esenciales o configurar. La AEPD
 *     sanciona los avisos donde "rechazar" queda escondido o en letra
 *     pequeña frente a un botón grande de "aceptar", así que aquí ambos son
 *     el mismo CTAButton.
 *  2. Panel de configuración, con un interruptor por categoría. Se abre
 *     desde el aviso simple, desde el enlace del pie de página o desde la
 *     propia Política de Cookies, en cualquier momento.
 *
 * Deliberadamente no hay una "X" para cerrar el aviso simple sin elegir:
 * cerrarlo sin decidir no equivale a ningún consentimiento y volvería a
 * aparecer en la siguiente visita, lo cual es más confuso que pedir una
 * elección explícita la primera vez.
 */
export default function CookieConsent() {
  const {
    bannerVisible,
    preferencesOpen,
    preferences,
    hasResponded,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  } = useCookieConsent();

  if (!bannerVisible && !preferencesOpen) return null;

  if (preferencesOpen) {
    return (
      <PreferencesPanel
        preferences={preferences}
        canDismiss={hasResponded}
        onClose={closePreferences}
        onSave={savePreferences}
        onAcceptAll={acceptAll}
        onRejectAll={rejectNonEssential}
      />
    );
  }

  return (
    <SimpleBanner
      onAcceptAll={acceptAll}
      onRejectAll={rejectNonEssential}
      onOpenPreferences={openPreferences}
    />
  );
}

function SimpleBanner({ onAcceptAll, onRejectAll, onOpenPreferences }) {
  return (
    <div
      role="region"
      aria-label="Aviso de cookies"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[70] flex justify-center p-4 sm:p-6"
    >
      <div className="w-full max-w-3xl bg-card text-foreground rounded-2xl border border-border shadow-hover p-5 sm:p-6 animate-modal-in">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex gap-3 sm:flex-1">
            <Cookie className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm leading-relaxed text-muted-foreground">
              Usamos cookies propias y de terceros para garantizar el funcionamiento de la
              web, analizar la navegación y mejorar tu experiencia. Puedes aceptarlas
              todas, rechazar las no esenciales o configurar tus preferencias. Más
              información en nuestra{" "}
              <Link
                to="/politica-de-cookies"
                className="text-primary underline underline-offset-2 focus-ring rounded"
              >
                Política de Cookies
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
            <button
              type="button"
              onClick={onOpenPreferences}
              data-testid="cookie-banner-configure"
              className="px-3 py-2 text-sm font-medium text-foreground underline underline-offset-2 hover:text-primary transition-colors duration-fast focus-ring rounded"
            >
              Configurar
            </button>
            <CTAButton
              variant="outline"
              size="sm"
              onClick={onRejectAll}
              data-testid="cookie-banner-reject"
            >
              Rechazar no esenciales
            </CTAButton>
            <CTAButton
              variant="primary"
              size="sm"
              onClick={onAcceptAll}
              data-testid="cookie-banner-accept-all"
            >
              Aceptar todas
            </CTAButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreferencesPanel({ preferences, canDismiss, onClose, onSave, onAcceptAll, onRejectAll }) {
  const [draft, setDraft] = useState(preferences);
  const dialogRef = useRef(null);
  const disparadorRef = useRef(null);
  const tituloId = useId();

  // Cada vez que se entra al panel se parte de la última decisión guardada
  // (o de todo desactivado salvo lo necesario, si aún no hay ninguna).
  useEffect(() => {
    setDraft(preferences);
  }, [preferences]);

  // Foco atrapado dentro del panel, cierre con Escape y devolución del foco
  // al elemento que lo abrió — mismo patrón que EnrollmentModal.
  useEffect(() => {
    disparadorRef.current = document.activeElement;
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const primero = dialogRef.current?.querySelector(SELECTOR_FOCUSABLE);
    primero?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        // Si todavía no hay una decisión tomada, Escape no cuenta como tal:
        // el panel se queda y solo se puede salir eligiendo una opción.
        if (!canDismiss) return;
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = Array.from(
        dialogRef.current?.querySelectorAll(SELECTOR_FOCUSABLE) || []
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;

      const primeroFoco = focusables[0];
      const ultimoFoco = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === primeroFoco) {
        e.preventDefault();
        ultimoFoco.focus();
      } else if (!e.shiftKey && document.activeElement === ultimoFoco) {
        e.preventDefault();
        primeroFoco.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previo;
      document.removeEventListener("keydown", onKeyDown);
      if (disparadorRef.current instanceof HTMLElement) disparadorRef.current.focus();
    };
  }, [onClose, canDismiss]);

  const toggle = (key) => setDraft((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm animate-fadeIn"
      onClick={() => {
        if (canDismiss) onClose();
      }}
      data-testid="cookie-preferences-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        className="bg-card text-foreground w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto animate-modal-in"
        onClick={(e) => e.stopPropagation()}
        data-testid="cookie-preferences-panel"
      >
        <div className="flex items-start justify-between gap-3 p-6 pb-4 border-b border-border sticky top-0 bg-card">
          <div className="flex items-start gap-3">
            <Cookie className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h2 id={tituloId} className="font-heading font-bold text-lg leading-tight">
                Preferencias de cookies
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Elige qué cookies permites. Puedes cambiarlo cuando quieras desde
                «Configuración de Cookies», en el pie de página.
              </p>
            </div>
          </div>
          {canDismiss && (
            <button
              type="button"
              onClick={onClose}
              data-testid="cookie-preferences-close"
              className="w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors duration-fast flex-shrink-0 focus-ring"
            >
              <X className="w-4 h-4" aria-hidden="true" />
              <span className="sr-only">Cerrar</span>
            </button>
          )}
        </div>

        <div className="p-6 space-y-5">
          {CATEGORIES.map((cat) => (
            <div key={cat.key} className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-sm">
                  {cat.title}
                  {cat.locked && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      (siempre activas)
                    </span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">{cat.description}</p>
              </div>
              <ToggleSwitch
                id={`cookie-cat-${cat.key}`}
                checked={cat.locked ? true : !!draft[cat.key]}
                disabled={cat.locked}
                onChange={() => toggle(cat.key)}
                label={cat.title}
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 p-6 pt-2 border-t border-border">
          <CTAButton
            variant="outline"
            size="sm"
            onClick={onRejectAll}
            data-testid="cookie-preferences-reject"
            className="sm:flex-1"
          >
            Rechazar no esenciales
          </CTAButton>
          <CTAButton
            variant="outline"
            size="sm"
            onClick={onAcceptAll}
            data-testid="cookie-preferences-accept-all"
            className="sm:flex-1"
          >
            Aceptar todas
          </CTAButton>
          <CTAButton
            size="sm"
            onClick={() => onSave(draft)}
            data-testid="cookie-preferences-save"
            className="sm:flex-1 font-bold"
          >
            Guardar preferencias
          </CTAButton>
        </div>
      </div>
    </div>
  );
}

function ToggleSwitch({ id, checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        "relative inline-flex flex-shrink-0 h-6 w-11 items-center rounded-full mt-0.5",
        "transition-colors duration-fast ease-out-apple focus-ring",
        checked ? "bg-primary" : "bg-black/15",
        disabled && "opacity-70 cursor-not-allowed"
      )}
    >
      <span
        className={cn(
          "inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-fast ease-out-apple",
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        )}
      />
    </button>
  );
}
